import {
    AccessReviewConnectorSourceValue,
    AccessReviewEntitlementRollupByAppValue,
    AccessReviewEntitlementRollupValue,
    AccessReviewValue,
    expandEntitlementRollup,
    expandEntitlementRollupByApp,
} from 'lib/actions/access_review'
import {
    AccessReviewSelectionValue,
    expandAccessReviewSelectionView,
} from 'lib/actions/access_review/access-review-selections'
import { ConnectorInstallationValue } from 'lib/actions/connector_catalog'
import { expandValue } from 'lib/rpcts'
import { ConflictMonitor } from 'pbts/c1/api/accessconflict/v1/accessconflict'
import { AccessViolationCountRefreshRef } from 'pbts/c1/api/accessconflict/v1/accessviolation'
import {
    AccessReview,
    AccessReviewByAccessConflictsServiceListRequest,
    AccessReviewConnectorSource,
    AccessReviewConnectorSourceRef,
    AccessReviewConnectorSourceView,
    AccessReviewRef,
    AccessReviewReportRef,
    AccessReviewSelectionRef,
    AccessReviewServiceGetResponse,
    AccessReviewServiceUpdateResponse,
    AccessReviewSetupAccessConflict,
    AccessReviewView,
} from 'pbts/c1/api/accessreview/v1/accessreview'
import {
    AccessReviewByAccessConflictsServiceClient,
    AccessReviewConnectorSourceServiceClient,
    AccessReviewReportServiceClient,
    AccessReviewServiceClient,
} from 'pbts/c1/api/accessreview/v1/accessreview.client'
import { AccessReviewActionsServiceClient } from 'pbts/c1/api/accessreview/v1/accessreview_actions.client'
import { AccessReviewSearchServiceEntitlementRollupRef } from 'pbts/c1/api/accessreview/v1/accessreview_search'
import { AccessReviewSearchServiceClient } from 'pbts/c1/api/accessreview/v1/accessreview_search.client'
import { AccessReviewSelectionsSearchServiceClient } from 'pbts/c1/api/accessreview/v1/accessreview_selections.client'
import { App } from 'pbts/c1/api/app/v1/app'
import { C1AppConfig, Connector, ConnectorSyncLifecycle, EnvConfig, FileConfig } from 'pbts/c1/api/app/v1/connector'
import { DashboardClient } from 'pbts/c1/api/dashboard/v1/private/dashboard.client'
import { File } from 'pbts/c1/api/filestore/v1/filestore'
import { Policy } from 'pbts/c1/api/policy/v1/policy'
import { TaskCountRefreshRef } from 'pbts/c1/api/task/v1/task'
import { User } from 'pbts/c1/api/user/v1/user'
import { Any } from 'pbts/google/protobuf/any'
import { createPagedListApi } from 'redux_toolkit/lib/createPagedListApi'
import { createUnaryApi } from 'redux_toolkit/lib/createUnaryApi'
import { createMutationApi } from './lib/createMutationApi'

const expandAccessReviewView = (msg: AccessReviewView | undefined, values: Any[]): AccessReviewValue => {
    const createdByUser = expandValue<User>(msg?.createdByUserPath || '', User, values)
    const policy = expandValue<Policy>(msg?.policyPath || '', Policy, values)
    return {
        ...AccessReview.create(msg?.accessReview ?? {}),
        createdByUser,
        policy,
        objectPermissions: msg?.objectPermissions,
    }
}

export const searchAccessReviewApi = createPagedListApi(
    AccessReviewSearchServiceClient,
    'search',
    (resp): Array<AccessReviewValue> => {
        return resp.list.map((v): AccessReviewValue => {
            return expandAccessReviewView(v, resp.expanded)
        })
    },
    AccessReviewRef.typeName,
)

export const listAccessReviewReportsApi = createPagedListApi(
    AccessReviewReportServiceClient,
    'list',
    undefined,
    AccessReviewReportRef.typeName,
)

export const searchAccessReviewSelectionsApi = createPagedListApi(
    AccessReviewSelectionsSearchServiceClient,
    'search',
    (resp): Array<AccessReviewSelectionValue> => {
        return resp.list.map((v) => expandAccessReviewSelectionView(v, resp.expanded))
    },
    AccessReviewSelectionRef.typeName,
)

export const getSelectionsStatusApi = createUnaryApi(AccessReviewSearchServiceClient, 'selectionsStatus', undefined, TaskCountRefreshRef.typeName)

export const entitlementRollupApi = createPagedListApi(
    AccessReviewSearchServiceClient,
    'entitlementRollup',
    (resp): Array<AccessReviewEntitlementRollupByAppValue> => {
        return resp.list.map((v) => expandEntitlementRollupByApp(v, resp.expanded))
    },
    AccessReviewSearchServiceEntitlementRollupRef.typeName,
)

export const entitlementRollupByAppApi = createPagedListApi(
    AccessReviewSearchServiceClient,
    'entitlementRollupByApp',
    (resp): Array<AccessReviewEntitlementRollupValue> => {
        return resp.list.map((v) => expandEntitlementRollup(v, undefined, resp.expanded))
    },
    AccessReviewSearchServiceEntitlementRollupRef.typeName,
)

export const getMyTasksCountApi = createUnaryApi(
    DashboardClient,
    'getTasksOverview',
    undefined,
    TaskCountRefreshRef.typeName,
)

export const getMyAccessCountApi = createUnaryApi(
    DashboardClient,
    'getAccessOverview',
    undefined,
    TaskCountRefreshRef.typeName,
)

export const getConflictsOverviewApi = createUnaryApi(
    DashboardClient,
    'getConflictsOverview',
    undefined,
    AccessViolationCountRefreshRef.typeName,
)

export const createAccessReviewApi = createMutationApi(
    AccessReviewServiceClient,
    'create',
)

export const updateAccessReviewApi = createMutationApi(
    AccessReviewServiceClient,
    'update',
    (res: AccessReviewServiceUpdateResponse): AccessReviewValue => expandAccessReviewViewDetails(res.accessReview, res.expanded),
    AccessReviewRef.typeName,
)

export const getAccessReviewApi = createUnaryApi(
    AccessReviewServiceClient,
    'get',
    (res: AccessReviewServiceGetResponse): AccessReviewValue => expandAccessReviewViewDetails(res.accessReview, res.expanded),
    AccessReviewRef.typeName,
)

const expandAccessReviewViewDetails = (msg: AccessReviewView | undefined, values: Any[]): AccessReviewValue => {
    const createdByUser = expandValue<User>(msg?.createdByUserPath || '', User, values)
    const policy = expandValue<Policy>(msg?.policyPath || '', Policy, values)
    return {
        ...AccessReview.create(msg?.accessReview ?? {}),
        createdByUser,
        policy,
        objectPermissions: msg?.objectPermissions,
    }
}

const expandAccessReviewConnectorSourceView = (
    msg: AccessReviewConnectorSourceView | undefined,
    values: Any[],
): ConnectorInstallationValue => {
    const connectorSource = AccessReviewConnectorSource.create(msg?.accessReviewConnectorSource ?? {})
    const resp: ConnectorInstallationValue = {
        id: connectorSource.connectorId,
        appId: connectorSource.appId,
        connectorId: connectorSource.connectorId,
        accessReviewConnectorSource: connectorSource,
        connector: expandValue<Connector>(msg?.connectorPath || '', Connector, values),
        app: expandValue<App>(msg?.appPath || '', App, values),
        connectorSyncLifecycle: expandValue<ConnectorSyncLifecycle>(msg?.syncLifecyclePath, ConnectorSyncLifecycle, values),
    }
    resp.configType = resp.connector?.config?.typeUrl
    if (resp.connector && resp.connector.config) {
        switch (Any.typeUrlToName(resp.connector.config.typeUrl)) {
            case FileConfig.typeName:
                resp.connector.config = Any.unpack<FileConfig>(resp.connector.config, FileConfig) as unknown as Any

                // We need to delete the binary file data here because Redux will complain if we put binary data in the
                // Redux state: "A non-serializable value was detected in the state"
                // eslint-disable-next-line no-case-declarations
                const config = resp?.connector.config as unknown as FileConfig
                delete (config?.file as Partial<File>).file
                break
            case EnvConfig.typeName:
                resp.connector.config = Any.unpack<EnvConfig>(resp.connector.config, EnvConfig) as unknown as Any
                break
            case C1AppConfig.typeName:
                resp.connector.config = Any.unpack<C1AppConfig>(resp.connector.config, C1AppConfig) as unknown as Any
                break
            default:
                break
        }
    }
    return resp
}

const expandAccessReviewAccuracyView = (
    msg: AccessReviewConnectorSourceView | undefined,
    values: Any[],
): AccessReviewConnectorSourceValue => {
    const resp: AccessReviewConnectorSourceValue = {
        ...AccessReviewConnectorSource.create(msg?.accessReviewConnectorSource ?? {}),
        connector: expandValue<Connector>(msg?.connectorPath || '', Connector, values),
        app: expandValue<App>(msg?.appPath || '', App, values),
    }
    if (resp.connector && resp.connector.config) {
        switch (Any.typeUrlToName(resp.connector.config.typeUrl)) {
            case FileConfig.typeName:
                resp.connector.config = Any.unpack<FileConfig>(resp.connector.config, FileConfig) as unknown as Any

                // We need to delete the binary file data here because Redux will complain if we put binary data in the
                // Redux state: "A non-serializable value was detected in the state"
                // eslint-disable-next-line no-case-declarations
                const config = resp?.connector.config as unknown as FileConfig
                delete (config?.file as Partial<File>).file
                break
            case EnvConfig.typeName:
                resp.connector.config = Any.unpack<EnvConfig>(resp.connector.config, EnvConfig) as unknown as Any
                break
            case C1AppConfig.typeName:
                resp.connector.config = Any.unpack<C1AppConfig>(resp.connector.config, C1AppConfig) as unknown as Any
                break
            default:
                break
        }
    }
    return resp
}

export const searchAccessReviewConnectorSourceApi = createPagedListApi(
    AccessReviewConnectorSourceServiceClient,
    'searchAccessReviewConnectorSources',
    (resp): Array<ConnectorInstallationValue> => resp.list.map(
        (v): ConnectorInstallationValue => expandAccessReviewConnectorSourceView(v, resp.expanded),
    ),
    AccessReviewConnectorSourceRef.typeName,
)

export const searchOutdatedConnectorSourcesApi = createPagedListApi(
    AccessReviewConnectorSourceServiceClient,
    'searchOutdatedAccessReviewConnectorSources',
    (resp): Array<AccessReviewConnectorSourceValue> => {
        return resp.list.map((v) => expandAccessReviewAccuracyView(v, resp.expanded))
    },
    AccessReviewConnectorSourceRef.typeName,
)

export const navFirstOpenSelection = createUnaryApi(
    AccessReviewSearchServiceClient,
    'navFirstOpenSelection',
)

export interface AccessReviewSetupAccessConflictValue extends AccessReviewSetupAccessConflict {
    conflictMonitor?: ConflictMonitor
}

const expandAccessReviewSetupAccessConflictView = (
    accessConflict: AccessReviewSetupAccessConflict | undefined,
    accessConflictPath: string | undefined,
    values: Any[],
): AccessReviewSetupAccessConflictValue => {
    const conflict = AccessReviewSetupAccessConflict.create(accessConflict ?? {})
    const conflictMonitor = expandValue<ConflictMonitor>(accessConflictPath || '', ConflictMonitor, values)

    return {
        ...conflict,
        conflictMonitor,
    }
}

export const listSelectedAccessConflictsApi = createPagedListApi(
    AccessReviewByAccessConflictsServiceClient,
    'list',
    (resp): Array<AccessReviewSetupAccessConflictValue> => {
        return resp.list.map(
            (v): AccessReviewSetupAccessConflictValue =>
                expandAccessReviewSetupAccessConflictView(v.accessConflict, v.accessConflictPath, resp.expanded),
        )
    },
    AccessReviewByAccessConflictsServiceListRequest.typeName,
)

export const setCampaignScopeByAccessConflictsAPI = createMutationApi(
    AccessReviewByAccessConflictsServiceClient,
    'setCampaignScopeByAccessConflict',
)

export const countOpenReviewsByUserApi = createPagedListApi(
    AccessReviewSearchServiceClient,
    'countOpenReviewsByUser',
    (response) => response.list,
)

export const getProgressByAppApi = createPagedListApi(
    AccessReviewSearchServiceClient,
    'getProgressByApp',
    (response) => response.list,
)

export const countCompleteReviewersApi = createUnaryApi(
    AccessReviewSearchServiceClient,
    'countCompleteReviewers',
)

export const countIncompleteReviewersApi = createUnaryApi(
    AccessReviewSearchServiceClient,
    'countIncompleteReviewers',
)

export const sendCampaignReminderApi = createMutationApi(
    AccessReviewActionsServiceClient,
    'campaignReminder',
)

export const getReviewerDecisionsApi = createPagedListApi(
    AccessReviewSearchServiceClient,
    'getReviewerDecisions',
    (response) => response.list,
)
